import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  GenericViewControllerService,
  GenericViewDataModel,
} from '@osos/shared/api';
import {
  BreadcrumbToken,
  DEFAULT_PAGESIZE,
  GENERIC_VIEW_ID,
  GenericViewPresenterModel,
  LoadingIndicator,
  ManagedHttpContext,
  PermissionScopeCounts,
  RefProperty,
  Where,
} from '@osos/shared/types';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import {
  GenericViewsFactoryService,
  PublicGenericViewsFactoryService,
} from '../../factories';
import { ApiUtilsService, BreadcrumbService } from '../../services';

import { LoadingActions } from '../loading/loading.actions';
import { NavigationStateActions } from '../navigation/navigation.actions';
import { GenericViewActions } from './generic-views.actions';

export interface GenericViewStateModel {
  presentedViews: GenericViewPresenterModel[];
  presentedView: { [key: string]: GenericViewPresenterModel };
  selectedView?: string;
  transitoryViews: GenericViewPresenterModel[];
  loading: LoadingIndicator;
  counts?: PermissionScopeCounts;
  defaultPageId: string;
  genericViewsReferences?: RefProperty[];
}

@State<GenericViewStateModel>({
  name: 'GenericViews',
  defaults: {
    presentedView: {},
    presentedViews: [],
    transitoryViews: [],
    loading: new LoadingIndicator(),
    counts: {
      anonymous: 0,
      authenticated: 0,
      restricted: 0,
    },
    defaultPageId: null,
    genericViewsReferences: [],
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class GenericViewsState {
  @Selector([GenericViewsState])
  static presentedViews(
    state: GenericViewStateModel,
  ): GenericViewPresenterModel[] {
    return state.presentedViews;
  }

  @Selector([GenericViewsState])
  static transitoryViews(
    state: GenericViewStateModel,
  ): GenericViewPresenterModel[] {
    return state.transitoryViews;
  }

  @Selector([GenericViewsState])
  static scopesCounts(state: GenericViewStateModel): PermissionScopeCounts {
    return state.counts;
  }

  @Selector([GenericViewsState])
  static selectedView(state: GenericViewStateModel): GenericViewPresenterModel {
    return state.presentedView[state.selectedView];
  }

  @Selector([GenericViewsState])
  static defaultPage(state: GenericViewStateModel): GenericViewPresenterModel {
    return state.presentedView[state.defaultPageId];
  }

  @Selector([GenericViewsState])
  static genericViewsReferences(state: GenericViewStateModel): RefProperty[] {
    return state.genericViewsReferences;
  }

  @Selector([GenericViewsState])
  static presentedView(
    viewId: string,
  ): (state: GenericViewStateModel) => GenericViewPresenterModel {
    return createSelector(
      [GenericViewsState],
      (state: GenericViewStateModel) => state.presentedView[viewId],
    );
  }

  constructor(
    private apiHelper: ApiUtilsService,
    private genericViewsFactoryService: GenericViewsFactoryService,
    private publicGenericViewsFactoryService: PublicGenericViewsFactoryService,
    private genericViewService: GenericViewControllerService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  @Action(GenericViewActions.FetchTransitoryViews)
  fetchTransitoryViews(
    { patchState }: StateContext<GenericViewStateModel>,
    { payload }: GenericViewActions.FetchTransitoryViews,
  ): Observable<any> {
    const where = {
      or: this.apiHelper.prepareLocalizedSearch(
        payload.searchTerm,
        payload.filterBy ?? [],
      ),
      ...payload.filters,
      ...(payload?.viewIds && {
        ['id']: {
          inq: payload.viewIds,
        },
      }),
    } as Where<GenericViewDataModel>;

    return this.genericViewService
      .find({
        ...this.apiHelper.prepareFilter({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? DEFAULT_PAGESIZE,
        }),
      })
      .pipe(
        tap((items) => {
          patchState({
            transitoryViews: items.map((v) =>
              this.genericViewsFactoryService.createPresenterModel(v),
            ),
          });
        }),
      );
  }

  @Action(GenericViewActions.FetchGenericViewsReferences)
  fetchGenericViewsReferences(
    { patchState }: StateContext<GenericViewStateModel>,
    { payload }: GenericViewActions.FetchGenericViewsReferences,
  ) {
    const where = payload.filters as Where<GenericViewDataModel>;

    return this.genericViewService
      .find({
        ...this.apiHelper.prepareFilter({
          where,
        }),
      })
      .pipe(
        map((items) =>
          items.map(
            (v) =>
              ({
                dataEntity: GENERIC_VIEW_ID,
                dataEntry: v.id,
                _resolved: {
                  mainTitle: v.name,
                },
              }) as RefProperty,
          ),
        ),
        tap((references) => {
          patchState({
            genericViewsReferences: references,
          });
        }),
      );
  }

  @Action(GenericViewActions.FetchViews)
  fetchViews(
    { dispatch, setState, patchState }: StateContext<GenericViewStateModel>,
    { payload }: GenericViewActions.FetchViews,
  ): Observable<any[]> {
    const where = {
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
      ...payload.filters,
    } as Where<GenericViewDataModel>;
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    return this.genericViewService
      .find({
        ...this.apiHelper.prepareFilter({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? DEFAULT_PAGESIZE,
        }),
      })
      .pipe(
        tap((items) => {
          patchState({
            presentedViews: items.map((v) =>
              this.genericViewsFactoryService.createPresenterModel(v),
            ),
          });
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
          setState(
            patch<GenericViewStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(GenericViewActions.FetchView)
  fetchView(
    { setState }: StateContext<GenericViewStateModel>,
    { viewId }: GenericViewActions.FetchView,
  ): Observable<GenericViewDataModel> {
    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.genericViewService.findById({ id: viewId }).pipe(
      tap((res) => {
        const presenter =
          this.genericViewsFactoryService.createPresenterModel(res);

        this.breadcrumbService.set(BreadcrumbToken.ViewName, presenter.name);
        setState(
          patch<GenericViewStateModel>({
            presentedView: patch({
              [viewId]: presenter,
            }),
          }),
        );
      }),
      finalize(() => {
        setState(
          patch<GenericViewStateModel>({
            loading: patch<LoadingIndicator>({
              item: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(GenericViewActions.FetchViewForEndUser)
  fetchViewForEndUser(
    { setState }: StateContext<GenericViewStateModel>,
    { viewId }: GenericViewActions.FetchViewForEndUser,
  ): Observable<GenericViewDataModel> {
    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.genericViewService.findByIdEndUser({ id: viewId }).pipe(
      tap((res) => {
        const presenter =
          this.genericViewsFactoryService.createPresenterModel(res);
        setState(
          patch<GenericViewStateModel>({
            presentedView: patch({
              [viewId]: presenter,
            }),
          }),
        );
      }),
      finalize(() => {
        setState(
          patch<GenericViewStateModel>({
            loading: patch<LoadingIndicator>({
              item: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(GenericViewActions.FetchViewForEndUserV2)
  fetchViewForEndUserV2(
    { setState }: StateContext<GenericViewStateModel>,
    { viewId }: GenericViewActions.FetchViewForEndUserV2,
  ): Observable<GenericViewPresenterModel> {
    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.genericViewService
      .findByIdEndUserV2({
        id: viewId,
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        map((res) => {
          return this.publicGenericViewsFactoryService.createPresenterModel(
            res,
          );
        }),
        tap((res) => {
          setState(
            patch<GenericViewStateModel>({
              presentedView: patch({
                [viewId]: res,
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<GenericViewStateModel>({
              loading: patch<LoadingIndicator>({
                item: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(GenericViewActions.FetchDefaultPage)
  fetchDefaultPage({
    setState,
    getState,
    patchState,
  }: StateContext<GenericViewStateModel>): Observable<any> {
    const currentDefaultPage =
      getState().presentedView[getState().defaultPageId];
    if (currentDefaultPage) {
      return EMPTY;
    }
    return this.genericViewService
      .getUserDefaultView({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((res) => {
          if (res) {
            const presenter =
              this.genericViewsFactoryService.createPresenterModel(res);
            setState(
              patch<GenericViewStateModel>({
                presentedView: patch({
                  [presenter.id]: presenter,
                }),
                defaultPageId: presenter.id,
              }),
            );
          } else {
            patchState({ defaultPageId: null });
          }
        }),
      );
  }

  @Action(GenericViewActions.SelectView)
  selectView(
    { getState, patchState }: StateContext<GenericViewStateModel>,
    { viewId }: GenericViewActions.SelectView,
  ): void {
    const selectedView = getState().presentedView[viewId];

    patchState({ selectedView: viewId });

    // Resolve breadcrumb tokens:
    this.breadcrumbService.set(
      BreadcrumbToken.GenericViewName,
      selectedView.name,
    );
  }

  @Action(GenericViewActions.CreateView)
  createView(
    { setState, dispatch }: StateContext<GenericViewStateModel>,
    { view }: GenericViewActions.CreateView,
  ): Observable<any> {
    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    return this.genericViewService
      .create({
        body: this.genericViewsFactoryService.createDataModel(view),
      })
      .pipe(
        tap((data) => {
          if (data) {
            const presenter =
              this.genericViewsFactoryService.createPresenterModel(data);
            setState(
              patch<GenericViewStateModel>({
                presentedView: patch({
                  [presenter.id]: presenter,
                }),
                defaultPageId: null,
              }),
            );
          }
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<GenericViewStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(GenericViewActions.UpdateView)
  updateView(
    { getState, setState, dispatch }: StateContext<GenericViewStateModel>,
    { viewId, view }: GenericViewActions.UpdateView,
  ): Observable<any> {
    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    return this.genericViewService
      .updateById({
        id: viewId,
        body: this.genericViewsFactoryService.createDataModel(view),
      })
      .pipe(
        tap(() => {
          setState(
            patch<GenericViewStateModel>({
              presentedViews: updateItem((v) => v.id === view.id, view),
              presentedView: patch({
                [viewId]: view,
              }),
              defaultPageId: null,
            }),
          );
        }),
        tap(() => {
          // Update/Remove the view in the sidebar:
          // if (this._shouldReloadNavigationItems(view, oldView)) {
          dispatch(new NavigationStateActions.FetchItemsPinnedToSidebarV2());
          // } else {
          //   if (
          //     view.isPinned &&
          //     view.isPublished &&
          //     view.authenticated?.enabled
          //   ) {
          //     const module =
          //       view.relatedTo?.dataEntry &&
          //       this.store.selectSnapshot(
          //         ModuleState.presentedModule(view.relatedTo.dataEntry)
          //       );

          //     const navItem: NavigationItem = {
          //       id: view.id,
          //       name: view.name,
          //       icon: view.icon,
          //       isPinned: view.isPinned,
          //       isPublished: view.isPublished,
          //       module: view.relatedTo?.dataEntry,
          //       relatedTo: view.relatedTo,
          //       type: 'GenericView',
          //     };

          //     dispatch(
          //       new NavigationStateActions.UpsertNavigationItem(navItem, module)
          //     );
          //   } else {
          //     dispatch(
          //       new NavigationStateActions.RemoveNavigationItem(
          //         view.id,
          //         view.relatedTo?.dataEntry
          //       )
          //     );
          //   }
          // }
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<GenericViewStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(GenericViewActions.GetCountsByTypes)
  getCountsByTypes(
    { setState }: StateContext<GenericViewStateModel>,
    { payload }: GenericViewActions.GetCountsByTypes,
  ): Observable<any> {
    const prepareWhere = (permissions: any) =>
      this.apiHelper.prepareWhere({
        or: this.apiHelper.prepareSearch(payload.searchTerm, [
          'name.en',
          'name.ar',
          'description.en',
          'description.ar',
        ]),
        ...payload.filter,
        and: [...permissions, ...(payload?.filter?.and ?? '')],
      });
    return forkJoin([
      this.genericViewService.count({
        where: prepareWhere([{ 'anonymous.enabled': true }]),
      }),
      this.genericViewService.count({
        where: prepareWhere([
          { 'authenticated.enabled': true },
          { 'authenticated.isRestricted': false },
        ]),
      }),
      this.genericViewService.count({
        where: prepareWhere([
          { 'authenticated.enabled': true },
          { 'authenticated.isRestricted': true },
        ]),
      }),
    ]).pipe(
      tap(([anonymous, authenticated, restricted]) => {
        setState(
          patch<GenericViewStateModel>({
            counts: {
              anonymous: anonymous.count,
              authenticated: authenticated.count,
              restricted: restricted.count,
            },
          }),
        );
      }),
    );
  }

  @Action(GenericViewActions.DeleteView)
  deleteView(
    { setState, dispatch }: StateContext<GenericViewStateModel>,
    { view }: GenericViewActions.DeleteView,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<GenericViewStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
        }),
      }),
    );
    return this.genericViewService.deleteById({ id: view?.id }).pipe(
      tap(() => {
        setState(
          patch<GenericViewStateModel>({
            presentedViews: removeItem((v) => v.id === view?.id),
            presentedView: patch({
              [view?.id]: undefined,
            }),
            defaultPageId: null,
          }),
        );

        // Remove from sidebar if it's already there:
        dispatch(
          new NavigationStateActions.RemoveNavigationItem(
            view?.id,
            view?.relatedTo?.dataEntry,
          ),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetCommandLoading(false));

        setState(
          patch<GenericViewStateModel>({
            loading: patch<LoadingIndicator>({
              remove: false,
            }),
          }),
        );
      }),
    );
  }
}
